import { formatPhone } from '@chiroup/core/functions/format';
import { EnvelopeIcon, GlobeAltIcon, MapIcon, PhoneIcon } from '@heroicons/react/24/outline';
import React, { useMemo } from 'react';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';

const FacebookIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
      clipRule="evenodd"
    />
  </svg>
);

const TwitterIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
  </svg>
);

const YouTubeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 01-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 01-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 011.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418zM15.194 12l-5.197 3V9l5.197 3z"
      clipRule="evenodd"
    />
  </svg>
);

const ClinicInfo: React.FC = () => {
  const { selectedPatient, language } = usePatientPortal();
  const clinic = selectedPatient?.clinic;
  const primaryColor = clinic?.primaryColor || '#77bd43';

  const isiOS = useMemo(() => {
    const ua = navigator.userAgent;

    return (
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }, []);

  const address = clinic?.address1
    ? `${clinic.address1}${clinic.address2 ? ` ${clinic.address2}` : ''}${
        clinic.city ? `, ${clinic.city}` : ''
      }${clinic.state ? ` ${clinic.state}` : ''}${clinic.zip ? ` ${clinic.zip}` : ''}`
    : '';
  const addressEncoded = address ? encodeURIComponent(address) : '';
  const directionsHref = address
    ? isiOS
      ? `http://maps.apple.com/?q=${addressEncoded}`
      : `https://www.google.com/maps/dir//${addressEncoded}`
    : '';

  const actions = useMemo(() => {
    if (!clinic) return [];
    const actionsArray: {
      key: 'phone' | 'directions' | 'email' | 'site';
      label: string;
      detail: string;
      actionText: string;
      href: string;
      icon: React.ReactNode;
      newTab?: boolean;
    }[] = [];

    if (clinic.phone) {
      const formattedPhone = formatPhone(clinic.phone, true) || '';
      actionsArray.push({
        key: 'phone',
        label: hcVerbiage.call[language],
        detail: formattedPhone,
        actionText: 'Call',
        href: `tel:${clinic.phone}`,
        icon: <PhoneIcon className="h-5 w-5" />,
      });
    }

    if (clinic.address1 && directionsHref) {
      actionsArray.push({
        key: 'directions',
        label: hcVerbiage.directions[language],
        detail: address,
        actionText: 'Get Directions',
        href: directionsHref,
        icon: <MapIcon className="h-5 w-5" />,
        newTab: true,
      });
    }

    if (clinic.email) {
      actionsArray.push({
        key: 'email',
        label: hcVerbiage.email[language],
        detail: clinic.email,
        actionText: 'Send Email',
        href: `mailto:${clinic.email}`,
        icon: <EnvelopeIcon className="h-5 w-5" />,
      });
    }

    if (clinic.site) {
      const siteWithoutProtocol = clinic.site.replace(/^https?:\/\//, '');
      actionsArray.push({
        key: 'site',
        label: hcVerbiage.site[language],
        detail: siteWithoutProtocol,
        actionText: 'Visit',
        href: `https://${siteWithoutProtocol}`,
        icon: <GlobeAltIcon className="h-5 w-5" />,
        newTab: true,
      });
    }

    return actionsArray;
  }, [clinic, language, address, directionsHref]);

  const socialLinks = useMemo(() => {
    if (!clinic) return [];
    const links: { label: string; href: string; icon: React.ReactNode }[] = [];

    if (clinic.facebook) {
      links.push({
        label: 'Facebook',
        href: clinic.facebook,
        icon: <FacebookIcon className="h-5 w-5" />,
      });
    }

    if (clinic.twitter) {
      links.push({
        label: 'Twitter',
        href: clinic.twitter,
        icon: <TwitterIcon className="h-5 w-5" />,
      });
    }

    if (clinic.youtube) {
      links.push({
        label: 'YouTube',
        href: clinic.youtube,
        icon: <YouTubeIcon className="h-5 w-5" />,
      });
    }

    return links;
  }, [clinic]);

  if (!clinic) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-gray-400 dark:text-gray-500">No clinic information available.</p>
      </div>
    );
  }

  const clinicName = clinic.name || hcVerbiage.myClinic[language];

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{clinicName}</h2>
        {selectedPatient?.primaryClinician && (
          <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
            {selectedPatient.primaryClinician}
          </p>
        )}
      </div>

      {actions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {actions.map(action => (
            <a
              key={action.key}
              href={action.href}
              target={action.newTab ? '_blank' : undefined}
              rel={action.newTab ? 'noopener noreferrer' : undefined}
              className="portal-card p-4 flex items-center gap-3 active:scale-[0.98] transition-transform"
            >
              <div
                className="flex size-10 shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
              >
                {action.icon}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {action.label}
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate mt-0.5">
                  {action.detail}
                </p>
              </div>
              <span className="shrink-0 text-xs font-semibold" style={{ color: primaryColor }}>
                {action.actionText}
              </span>
            </a>
          ))}
        </div>
      )}

      {socialLinks.length > 0 && (
        <div className="portal-card p-4 flex items-center justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Follow the clinic
          </p>
          <div className="flex items-center gap-3">
            {socialLinks.map(social => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="flex size-9 items-center justify-center rounded-xl text-white transition-opacity active:opacity-80"
                style={{ backgroundColor: primaryColor }}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicInfo;
